import { type ReactNode, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Building2,
  KeyRound,
  LayoutDashboard,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  User,
  type LucideIcon,
} from 'lucide-react';
import { Button, cn } from '@/components/ui';
import { useAuthStore } from '@/store/auth.store';
import { ThemeToggle } from '@/components/layout/ThemeToggle';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/tenant', label: 'Tenant', icon: Building2 },
  { to: '/license', label: 'License', icon: KeyRound },
  { to: '/settings', label: 'Settings', icon: Settings },
];

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
    <div className="flex h-screen overflow-hidden bg-surface-base text-text-primary">
      <aside
        className={cn(
          'flex shrink-0 flex-col border-r border-border-subtle bg-surface-0 transition-all duration-[var(--duration-slow)]',
          sidebarOpen ? 'w-60' : 'w-16',
        )}
      >
        {/* Branding + collapse toggle */}
        <div
          className={cn(
            'flex h-12 items-center justify-between border-b border-border-subtle transition-all duration-[var(--duration-slow)]',
            sidebarOpen ? 'gap-2 px-3' : 'px-1.5',
          )}
        >
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent/15 text-accent">
            <span className="text-sm font-bold">D</span>
          </div>
          {sidebarOpen && (
            <span className="flex-1 truncate text-sm font-semibold">Deployment Server</span>
          )}
          <button
            onClick={() => setSidebarOpen((o) => !o)}
            className="inline-flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-sm text-text-secondary transition-colors hover:bg-surface-2 hover:text-text-primary"
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className={cn('flex-1 overflow-y-auto py-3', sidebarOpen ? 'px-2' : 'px-1.5')}>
          <ul className="space-y-0.5">
            {NAV_ITEMS.map((item) => (
              <li key={item.to} title={sidebarOpen ? undefined : item.label}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex w-full items-center gap-2.5 rounded-sm transition-colors duration-[var(--duration-fast)]',
                      sidebarOpen ? 'px-2 py-1.5' : 'justify-center px-2.5 py-2.5',
                      isActive
                        ? 'bg-accent/10 text-accent'
                        : 'text-text-secondary hover:bg-surface-1 hover:text-text-primary',
                    )
                  }
                >
                  <item.icon className="h-3.5 w-3.5 shrink-0" />
                  {sidebarOpen && <span className="text-xs font-medium">{item.label}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer user row */}
        <div
          className="space-y-1 border-t border-border-subtle transition-all duration-[var(--duration-slow)]"
          style={{ padding: sidebarOpen ? '0.5rem' : '0.375rem' }}
        >
          {sidebarOpen ? (
            <>
              <div className="flex items-center gap-2 rounded-sm bg-surface-1 px-2 py-1.5">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface-2">
                  <User className="h-3.5 w-3.5 text-text-tertiary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-text-primary">{user?.username ?? 'User'}</p>
                  <p className="truncate text-[10px] text-text-tertiary">{user?.email ?? ''}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                fullWidth
                leadingIcon={<LogOut className="h-3.5 w-3.5" />}
                onClick={() => logout()}
                className="justify-start text-state-danger hover:bg-state-danger/10 hover:text-state-danger"
              >
                Log out
              </Button>
            </>
          ) : (
            <button
              onClick={() => logout()}
              className="inline-flex h-9 w-full items-center justify-center rounded-sm text-state-danger transition-colors hover:bg-state-danger/10"
              aria-label="Log out"
              title="Log out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          )}
        </div>
      </aside>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-12 shrink-0 items-center justify-end gap-2 border-b border-border-subtle bg-surface-0 px-4">
          <ThemeToggle />
        </header>
        <main className="flex-1 overflow-y-auto p-6">{children}</main>
      </div>
    </div>
  );
}
